import React, { useEffect, useState } from "react";
import {
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions,
  ListboxProps
} from "@headlessui/react";
import { LockClosedIcon } from "@heroicons/react/solid";
import { RiVipCrown2Line } from "@remixicon/react";
import { PureTooltip } from "components/Tooltip";
import RotatingArrowIcon from "components/RotatingArrowIcon";
import Badge from "components/Badge";
import { MENU_AND_DROPDOWN_TRANSITION } from "misc/transitionProperties";
import { Sizes } from "styles/sizes";
import { cx } from "utils";


type SelectSizes = Sizes<{ button: string, icon: string, option: string }>;

const selectSizes: SelectSizes = {
  SM: {
    button: "h-8 px-2 gap-x-2 text-sm",
    icon: "w-4 h-4",
    option: "py-[5px] px-3 text-base"
  },
  MD: {
    button: "h-10 px-2 gap-x-2 text-base",
    icon: "",
    option: "py-[5px] px-3 text-base"
  },
  LG: {
    button: "h-12 px-12 text-base",
    icon: "",
    option: "py-[5px] px-3 text-base"
  }
};

export type SelectOption<V> = {
  name: string;
  value: V;
  group?: string;
  beta?: boolean;
};

interface ISelectProps<T> {
  value: T;
  size?: keyof SelectSizes;
  Icon?: React.ComponentType<any>;
  options: T[];
  className?: string;
  optionsClassName?: string;
  buttonClassName?: string;
  onChange?: (value: T) => void;
  buttonTestId?: string;
  locked?: boolean;
  tooltip?: string;
  premium?: boolean;
}

export type SelectProps<T> = ISelectProps<T> & Pick<ListboxProps, "name">;

export default function Select<T extends SelectOption<any> | string>(
  {
    value,
    name,
    options,
    size = "MD",
    className = "text-nowrap",
    optionsClassName = "max-h-52",
    buttonClassName = "w-full",
    Icon,
    onChange,
    buttonTestId,
    locked,
    tooltip,
    premium,
  }: SelectProps<T>
) {
  const nameAccessor = (option: T | string) => typeof option === "string" ? option as string : (option as SelectOption<any>).name;
  const groupAccessor = (option: T | string | undefined) => (option === undefined || typeof option === "string") ? undefined : (option as SelectOption<any>).group;
  const betaAccessor = (option: T | string) => typeof option === "string" ? false : (option as SelectOption<any>).beta;

  const [ selectedValue, setSelectedValue ] = useState(value);
  const selectSizeStyles = selectSizes[size];

  useEffect(() => {
    setSelectedValue(value);
  }, [ value ]);

  useEffect(() => {
    onChange?.(selectedValue);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ selectedValue ]);  // onChange is left out on purpose, otherwise the effect will be called in an infinite loop


  return (
    <div className={ cx("relative", className) }>
      <Listbox
        name={ name }
        value={ selectedValue }
        onChange={ setSelectedValue }
        by={ (o1: T, o2: T) => nameAccessor(o1) === nameAccessor(o2) }
        disabled={ locked }
      >
        <ListboxButton className={ buttonClassName } data-testid={ buttonTestId }>
          { ({ open, value }) =>
            <div className={ cx(
              "select select-button flex items-center justify-between",
              selectSizeStyles?.button,
              locked && "cursor-not-allowed"
            ) }
            >
              <div className={ cx("flex flex-row gap-x-2 items-center", locked && "opacity-50") }>
                { Icon && <Icon className={ cx("select-icon", selectSizeStyles?.icon) }/> }
                { nameAccessor(value) }
                { betaAccessor(value) && <Badge text="Beta" className="flex-shrink-0" /> }
              </div>
              <div className="flex items-center gap-x-1">
                <RotatingArrowIcon direction="down" rotated={ open } className="size-4 text-gray-600 dark:text-neutral-500" />
                { (locked || premium) ? (
                  <PureTooltip content={ tooltip }>
                    { locked
                      ? <LockClosedIcon className="w-4 h-4 text-gray-500 opacity-50" />
                      : <RiVipCrown2Line className="w-4 h-4 text-gray-500 opacity-50" />
                    }
                  </PureTooltip>
                ) : null }
              </div>
            </div>
          }
        </ListboxButton>
        <ListboxOptions
          transition
          className={ cx(
            "absolute border dark:rounded w-full bg-white drop-shadow mt-1 z-10 origin-top-right overflow-auto",
            optionsClassName,
            MENU_AND_DROPDOWN_TRANSITION
          ) }
          portal={ false }
        >
          { options.map((option, index) => {
            const group = groupAccessor(option);
            const showGroupHeader = group !== undefined && group !== groupAccessor(options[index - 1]);

            return (
              <React.Fragment key={ nameAccessor(option) }>
                { showGroupHeader &&
                  <div className="px-3 pt-3 pb-1 text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-neutral-500 select-none">
                    { group }
                  </div>
                }
                <ListboxOption
                  value={ option }
                  className={ cx(
                    selectSizeStyles?.option,
                    "transition-colors duration-150 flex items-center justify-between",
                    "data-[selected]:!bg-primary-600 data-[selected]:text-white",
                    "data-[focus]:bg-primary-400 data-[focus]:text-white",
                    "dark:data-[selected]:!bg-primary-200 dark:data-[selected]:font-medium dark:data-[selected]:text-neutral-900",
                    "dark:data-[focus]:bg-primary-100 dark:data-[focus]:text-neutral-900"
                  ) }
                >
                  <span>{ nameAccessor(option) }</span>
                  { betaAccessor(option) && <Badge text="Beta" className="ml-2 flex-shrink-0" /> }
                </ListboxOption>
              </React.Fragment>
            );
          }) }
        </ListboxOptions>
      </Listbox>
    </div>
  );
};
